import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Select from 'react-select';

import StandardLayout from '@/common/layouts/StandardLayout';
import DataTable from '@/common/tableData/DataTable';
import Paginator from '@/pages/common/paginator';
import { formatDateYyyyMmDd } from '@/common/utils/format-date';
import {
  createAgentAssociationMigrationRest,
  downloadAgentAssociationMigrationCsvRest,
  fetchAgentAssociationMigrationBatchRest,
  fetchAgentAssociationMigrationBatchesRest,
  fetchAgentsRest,
  fetchMigrationVerificationRest,
  processMigrationBatchRest,
  searchUsersByAliasesRest,
} from '@/services/agents/agents-service';
import {
  AgentAssociationMigrationBatchStatus,
  AgentAssociationMigrationUserStatus,
  MigrationVerificationUserTree,
  SearchUserForMigrationDto,
} from '@repo/common-types';
import { Progress } from 'reactstrap';

const VerificationTreeNode: React.FC<{
  node: MigrationVerificationUserTree;
  level: number;
}> = ({ node, level }) => {
  const [expanded, setExpanded] = useState(level < 2);
  const allPassed = node.depthPassed && node.agreementPassed && node.historyPassed;
  const hasChildren = node.children.length > 0;

  // Build status text for hover tooltip
  const getStatusDetails = () => {
    const checks = [
      `Depth: ${node.beforeDepth} → ${node.actualDepth}${!node.depthPassed ? ` (expected ${node.expectedDepth})` : ''}`,
    ];
    if (node.isRootUser) {
      checks.push(`Agreement: ${node.agreementPassed ? 'OK' : 'Failed'}`);
      checks.push(
        `History: ${node.isReferralQualified ? (node.historyPassed ? 'OK' : 'Failed') : 'N/A (not qualified)'}`,
      );
    }
    return checks.join('\n');
  };

  return (
    <div style={{ marginLeft: level * 20 }}>
      <div className="d-flex align-items-center gap-2 py-1" title={getStatusDetails()} style={{ cursor: 'default' }}>
        {/* Tree line + expand toggle */}
        {hasChildren ? (
          <button
            className="btn btn-link text-muted p-0"
            onClick={() => setExpanded(!expanded)}
            style={{ width: 16, textDecoration: 'none' }}
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className="text-muted" style={{ width: 16, textAlign: 'center' }}>
            •
          </span>
        )}

        {/* Status indicator */}
        <span
          className={`rounded-circle d-inline-block ${allPassed ? 'bg-success' : 'bg-danger'}`}
          style={{ width: 8, height: 8, flexShrink: 0 }}
        />

        {/* User name */}
        <span className={node.isRootUser ? 'fw-medium' : ''}>{node.alias}</span>

        {/* Minimal depth indicator */}
        <span className="text-muted small">
          depth {node.beforeDepth} → {node.actualDepth}
        </span>

        {/* Only show issues inline */}
        {!allPassed && (
          <span className="text-danger small">
            {!node.depthPassed && '(depth mismatch)'}
            {node.isRootUser && !node.agreementPassed && '(agreement)'}
            {node.isRootUser && node.isReferralQualified && !node.historyPassed && '(history)'}
          </span>
        )}

        {/* Subtle qualifier for root users */}
        {node.isRootUser && !node.isReferralQualified && (
          <span className="text-muted small fst-italic">not qualified</span>
        )}
      </div>

      {hasChildren && (
        <Collapse isOpen={expanded}>
          {node.children.map((child) => (
            <VerificationTreeNode key={child.userId} node={child} level={level + 1} />
          ))}
        </Collapse>
      )}
    </div>
  );
};

const QuickMigrationCard: React.FC<{ onMigrationCreated: () => void }> = ({ onMigrationCreated }) => {
  const [aliasInput, setAliasInput] = useState('');
  const [players, setPlayers] = useState<SearchUserForMigrationDto[]>([]);
  const [destinationSearch, setDestinationSearch] = useState('');
  const [destinationAgent, setDestinationAgent] = useState<{ value: string; label: string } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: agentsData, isLoading: isLoadingAgents } = useQuery({
    queryKey: ['agents', destinationSearch],
    queryFn: () => fetchAgentsRest({ skip: 0, take: 100, filter: { search: destinationSearch } }),
    staleTime: 30_000,
  });

  const searchMutation = useMutation({
    mutationFn: searchUsersByAliasesRest,
    onSuccess: (data) => {
      if (data.notFound.length > 0) {
        toast.warning(`Player "${data.notFound[0]}" not found`);
        return;
      }
      if (data.found.length === 0) return;
      const newPlayer = data.found[0];
      if (players.some((p) => p.id === newPlayer.id)) {
        toast.info(`${newPlayer.alias} already added`);
        return;
      }
      setPlayers((prev) => [...prev, newPlayer]);
      setAliasInput('');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Search failed'),
  });

  const handleAddPlayer = () => {
    const alias = aliasInput.trim();
    if (!alias) return;
    searchMutation.mutate([alias]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPlayer();
    }
  };

  const removePlayer = (userId: string) => {
    setPlayers((prev) => prev.filter((p) => p.id !== userId));
  };

  const playersBySource = useMemo(() => {
    const groups = new Map<string, SearchUserForMigrationDto[]>();
    for (const p of players) {
      const key = p.currentAgent?.id ?? 'no-agent';
      const list = groups.get(key) || [];
      list.push(p);
      groups.set(key, list);
    }
    return groups;
  }, [players]);

  const handleConfirmMigration = async () => {
    if (!destinationAgent || players.length === 0) return;

    setIsSubmitting(true);
    let successCount = 0;

    for (const [sourceKey, sourcePlayers] of playersBySource) {
      try {
        await createAgentAssociationMigrationRest({
          sourceAgentId: sourceKey === 'no-agent' ? null : sourceKey,
          destinationAgentId: destinationAgent.value,
          userIds: sourcePlayers.map((p) => p.id),
        });
        successCount++;
      } catch (e) {
        toast.error(`Failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
      }
    }

    setIsSubmitting(false);
    if (successCount > 0) {
      toast.success(`Queued ${successCount} migration batch(es)`);
      setPlayers([]);
      setDestinationAgent(null);
      setShowConfirm(false);
      onMigrationCreated();
    }
  };

  const destinationOptions = useMemo(() => {
    const opts =
      agentsData?.agents.map((a) => ({
        value: a.id,
        label: a.alias,
      })) || [];
    if (destinationAgent && !opts.find((o) => o.value === destinationAgent.value)) {
      opts.unshift(destinationAgent);
    }
    return opts;
  }, [agentsData, destinationAgent]);

  return (
    <Card className="mb-4">
      <CardHeader className="fw-bold">Quick Migration</CardHeader>
      <CardBody>
        <div className="d-flex mb-3 gap-2">
          <Input
            placeholder="Enter player alias"
            value={aliasInput}
            onChange={(e) => setAliasInput(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ maxWidth: 300 }}
          />
          <Button color="primary" onClick={handleAddPlayer} disabled={searchMutation.isPending || !aliasInput.trim()}>
            {searchMutation.isPending ? '...' : 'Add'}
          </Button>
        </div>

        {players.length > 0 && (
          <>
            <Table size="sm" bordered responsive className="mb-3">
              <thead>
                <tr>
                  <th>Alias</th>
                  <th>Current Agent</th>
                  <th>Depth</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {players.map((u) => (
                  <tr key={u.id}>
                    <td>{u.alias}</td>
                    <td>{u.currentAgent?.alias ?? <span className="text-muted">No Agent</span>}</td>
                    <td>{u.referralDepth}</td>
                    <td>
                      <Button size="sm" color="link" className="text-danger p-0" onClick={() => removePlayer(u.id)}>
                        ✕
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="d-flex align-items-end gap-3">
              <div style={{ minWidth: 250 }}>
                <label className="form-label small mb-1">Destination Agent</label>
                <Select
                  isLoading={isLoadingAgents}
                  options={destinationOptions}
                  value={destinationAgent}
                  onChange={(opt) => setDestinationAgent(opt)}
                  onInputChange={(val, { action }) => {
                    if (action === 'input-change') setDestinationSearch(val);
                  }}
                  placeholder="Search agent..."
                  isClearable
                />
              </div>
              <Button
                color="success"
                onClick={() => setShowConfirm(true)}
                disabled={players.length === 0 || !destinationAgent}
              >
                Queue Migration ({players.length})
              </Button>
              <Button outline color="secondary" onClick={() => setPlayers([])}>
                Clear All
              </Button>
            </div>
          </>
        )}

        <Modal isOpen={showConfirm} toggle={() => !isSubmitting && setShowConfirm(false)} centered>
          <ModalHeader toggle={() => !isSubmitting && setShowConfirm(false)}>Confirm Migration</ModalHeader>
          <ModalBody>
            <p>
              Migrate <strong>{players.length}</strong> player(s) to <strong>{destinationAgent?.label}</strong>?
            </p>
            {playersBySource.size > 1 && (
              <p className="text-muted small">
                Players from {playersBySource.size} different sources will create {playersBySource.size} separate
                batches.
              </p>
            )}
            <p className="text-muted small mb-0">Effective from the start of next week.</p>
          </ModalBody>
          <ModalFooter>
            <Button outline color="dark" onClick={() => setShowConfirm(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button color="success" onClick={handleConfirmMigration} disabled={isSubmitting}>
              {isSubmitting ? 'Queueing...' : 'Confirm'}
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

const formatPlayersList = (aliases: string[] | undefined, maxDisplay = 3): string => {
  if (!aliases || aliases.length === 0) return '-';
  if (aliases.length <= maxDisplay) return aliases.join(', ');
  return `${aliases.slice(0, maxDisplay).join(', ')}... (+${aliases.length - maxDisplay})`;
};

const MigrationProgressModal: React.FC<{
  batchId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}> = ({ batchId, isOpen, onClose, onComplete }) => {
  const [hasStarted, setHasStarted] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);

  const { data: batchDetail, isLoading } = useQuery({
    queryKey: ['migrationProgress', batchId],
    queryFn: () => fetchAgentAssociationMigrationBatchRest(batchId as string),
    enabled: Boolean(batchId) && isOpen,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling when completed or failed
      if (
        status === AgentAssociationMigrationBatchStatus.completed ||
        status === AgentAssociationMigrationBatchStatus.failed
      ) {
        return false;
      }
      return 1500; // Poll every 1.5 seconds
    },
  });

  // Start processing when modal opens
  React.useEffect(() => {
    if (isOpen && batchId && !hasStarted) {
      setHasStarted(true);
      setProcessingError(null);
      processMigrationBatchRest(batchId).catch((e) => {
        setProcessingError(e instanceof Error ? e.message : 'Processing failed');
      });
    }
  }, [isOpen, batchId, hasStarted]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setHasStarted(false);
      setProcessingError(null);
    }
  }, [isOpen]);

  // Notify parent when complete
  React.useEffect(() => {
    if (
      batchDetail?.status === AgentAssociationMigrationBatchStatus.completed ||
      batchDetail?.status === AgentAssociationMigrationBatchStatus.failed
    ) {
      onComplete();
    }
  }, [batchDetail?.status, onComplete]);

  const users = batchDetail?.selectedRootUsers ?? [];
  const completedCount = users.filter((u) => u.status === AgentAssociationMigrationUserStatus.completed).length;
  const failedCount = users.filter((u) => u.status === AgentAssociationMigrationUserStatus.failed).length;
  const pendingCount = users.filter((u) => u.status === AgentAssociationMigrationUserStatus.pending).length;
  const processingCount = users.filter((u) => u.status === AgentAssociationMigrationUserStatus.processing).length;
  const totalCount = users.length;
  const progressPercent = totalCount > 0 ? Math.round(((completedCount + failedCount) / totalCount) * 100) : 0;

  const isComplete = batchDetail?.status === AgentAssociationMigrationBatchStatus.completed;
  const isFailed = batchDetail?.status === AgentAssociationMigrationBatchStatus.failed;
  const isProcessing = batchDetail?.status === AgentAssociationMigrationBatchStatus.processing;

  const getStatusIcon = (status: AgentAssociationMigrationUserStatus) => {
    switch (status) {
      case AgentAssociationMigrationUserStatus.completed:
        return <span className="text-success">✓</span>;
      case AgentAssociationMigrationUserStatus.failed:
        return <span className="text-danger">✗</span>;
      case AgentAssociationMigrationUserStatus.processing:
        return <span className="text-primary">⟳</span>;
      default:
        return <span className="text-muted">○</span>;
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered backdrop="static" keyboard={false}>
      <ModalHeader>
        {isComplete ? '✓ Migration Complete' : isFailed ? '✗ Migration Failed' : 'Processing Migration...'}
      </ModalHeader>
      <ModalBody>
        {isLoading && !batchDetail ? (
          <Alert color="info">Starting migration...</Alert>
        ) : processingError ? (
          <Alert color="danger">{processingError}</Alert>
        ) : (
          <>
            {/* Progress bar */}
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="small text-muted">
                  {isComplete ? 'Completed' : isProcessing ? 'Processing...' : batchDetail?.status}
                </span>
                <span className="small fw-bold">
                  {completedCount + failedCount} / {totalCount}
                </span>
              </div>
              <Progress
                value={progressPercent}
                color={isFailed ? 'danger' : isComplete ? 'success' : 'primary'}
                animated={isProcessing}
                striped={isProcessing}
              />
            </div>

            {/* Status summary */}
            <div className="d-flex justify-content-center mb-3 gap-4">
              <div className="text-center">
                <div className="fs-4 fw-bold text-success">{completedCount}</div>
                <div className="small text-muted">Completed</div>
              </div>
              <div className="text-center">
                <div className="fs-4 fw-bold text-danger">{failedCount}</div>
                <div className="small text-muted">Failed</div>
              </div>
              <div className="text-center">
                <div className="fs-4 fw-bold text-primary">{processingCount}</div>
                <div className="small text-muted">Processing</div>
              </div>
              <div className="text-center">
                <div className="fs-4 fw-bold text-secondary">{pendingCount}</div>
                <div className="small text-muted">Pending</div>
              </div>
            </div>

            {/* Batch error */}
            {batchDetail?.error && (
              <Alert color="danger" className="mb-3">
                {batchDetail.error}
              </Alert>
            )}

            {/* User list */}
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <Table size="sm" className="mb-0">
                <tbody>
                  {users.map((user) => (
                    <tr key={user.userId}>
                      <td style={{ width: 30 }}>{getStatusIcon(user.status)}</td>
                      <td>{user.alias}</td>
                      <td className="text-muted small">{user.status}</td>
                      {user.error && (
                        <td className="text-danger small" title={user.error}>
                          {user.error.substring(0, 30)}...
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color={isComplete ? 'success' : isFailed ? 'danger' : 'secondary'}
          onClick={onClose}
          disabled={isProcessing && !processingError}
        >
          {isComplete || isFailed ? 'Close' : 'Processing...'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const AgentMigrationsPage: React.FC = () => {
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(25);
  const [status, setStatus] = useState<AgentAssociationMigrationBatchStatus | ''>('');

  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [showVerification, setShowVerification] = useState(false);

  const {
    data: batches,
    error,
    refetch,
  } = useQuery({
    queryKey: ['agentAssociationMigrations', skip, take, status],
    queryFn: () => fetchAgentAssociationMigrationBatchesRest({ skip, take, ...(status ? { status } : {}) }),
    staleTime: 30_000,
  });

  const { data: batchDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['agentAssociationMigrationBatch', selectedBatchId],
    queryFn: () => fetchAgentAssociationMigrationBatchRest(selectedBatchId as string),
    enabled: Boolean(selectedBatchId),
    staleTime: 10_000,
  });

  const { data: verification, isLoading: isLoadingVerification } = useQuery({
    queryKey: ['migrationVerification', selectedBatchId],
    queryFn: () => fetchMigrationVerificationRest(selectedBatchId as string),
    enabled: Boolean(selectedBatchId) && showVerification,
    staleTime: 30_000,
  });

  const closeModal = useCallback(() => {
    setSelectedBatchId(null);
    setShowVerification(false);
  }, []);

  const handleExport = useCallback(async (batchId: string) => {
    try {
      await downloadAgentAssociationMigrationCsvRest(batchId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to export');
    }
  }, []);

  const [processingBatchId, setProcessingBatchId] = useState<string | null>(null);

  const handleProcessComplete = useCallback(() => {
    refetch();
  }, [refetch]);

  const closeProgressModal = useCallback(() => {
    setProcessingBatchId(null);
    // Also close the detail modal if open
    if (selectedBatchId === processingBatchId) {
      setSelectedBatchId(null);
      setShowVerification(false);
    }
  }, [selectedBatchId, processingBatchId]);

  const columns = useMemo(
    () => [
      {
        header: 'Effective From',
        accessorKey: 'effectiveFrom',
        enableSorting: false,
        cell: ({ row }) => formatDateYyyyMmDd(row.original.effectiveFrom),
      },
      { header: 'Status', accessorKey: 'status', enableSorting: false },
      {
        header: 'Source',
        accessorKey: 'sourceAgent',
        enableSorting: false,
        cell: ({ row }) => row.original.sourceAgent?.alias ?? 'No Agent',
      },
      {
        header: 'Destination',
        accessorKey: 'destinationAgent',
        enableSorting: false,
        cell: ({ row }) => row.original.destinationAgent?.alias ?? 'No Agent',
      },
      {
        header: 'Players',
        accessorKey: 'selectedRootUserAliases',
        enableSorting: false,
        cell: ({ row }) => formatPlayersList(row.original.selectedRootUserAliases),
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        enableSorting: false,
        cell: ({ row }) => formatDateYyyyMmDd(row.original.createdAt),
      },
      {
        header: 'Processed',
        accessorKey: 'processedAt',
        enableSorting: false,
        cell: ({ row }) => formatDateYyyyMmDd(row.original.processedAt),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button size="sm" outline color="dark" onClick={() => setSelectedBatchId(row.original.id)}>
              View
            </Button>
            <Button size="sm" outline color="dark" onClick={() => handleExport(row.original.id)}>
              Export
            </Button>
          </div>
        ),
      },
    ],
    [handleExport],
  );

  return (
    <StandardLayout pageTitle="Agent Migrations">
      {error ? <Alert color="danger">Unable to load migration batches</Alert> : null}

      <QuickMigrationCard onMigrationCreated={refetch} />

      <div className="d-flex align-items-center mb-3 gap-2">
        <label className="form-label mb-0">Status</label>
        <select
          className="form-select w-auto"
          value={status}
          onChange={(e) => {
            setSkip(0);
            setStatus(e.target.value as AgentAssociationMigrationBatchStatus | '');
          }}
        >
          <option value="">All</option>
          <option value={AgentAssociationMigrationBatchStatus.pending}>
            {AgentAssociationMigrationBatchStatus.pending}
          </option>
          <option value={AgentAssociationMigrationBatchStatus.processing}>
            {AgentAssociationMigrationBatchStatus.processing}
          </option>
          <option value={AgentAssociationMigrationBatchStatus.completed}>
            {AgentAssociationMigrationBatchStatus.completed}
          </option>
          <option value={AgentAssociationMigrationBatchStatus.failed}>
            {AgentAssociationMigrationBatchStatus.failed}
          </option>
        </select>
        <Button
          outline
          color="dark"
          onClick={() => {
            setSkip(0);
            setStatus('');
            refetch();
          }}
        >
          Clear
        </Button>
      </div>

      <DataTable title="Migration Batches" data={batches?.items || []} columns={columns} enableSorting={false} />

      <Paginator
        take={take}
        skip={skip}
        showing={batches?.items?.length || 0}
        totalCount={batches?.totalCount || 0}
        setTake={(nextTake) => {
          setTake(nextTake);
          setSkip(0);
        }}
        setSkip={(nextSkip) => setSkip(nextSkip)}
      />

      <Modal isOpen={Boolean(selectedBatchId)} toggle={closeModal} centered size="xl">
        <ModalHeader toggle={closeModal}>Migration Details</ModalHeader>
        <ModalBody>
          {isLoadingDetail ? (
            <Alert color="info">Loading...</Alert>
          ) : !batchDetail ? (
            <Alert color="danger">Unable to load details</Alert>
          ) : (
            <>
              <div className="mb-2">
                <strong>Status:</strong> {batchDetail.status}
              </div>
              <div className="mb-2">
                <strong>Effective from:</strong> {formatDateYyyyMmDd(batchDetail.effectiveFrom)}
              </div>
              <div className="mb-2">
                <strong>Source:</strong> {batchDetail.sourceAgent?.alias}
              </div>
              <div className="mb-3">
                <strong>Destination:</strong> {batchDetail.destinationAgent?.alias ?? 'No Agent'}
              </div>

              <Table size="sm" bordered responsive>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Agent</th>
                    <th>Status</th>
                    <th>Processed</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {batchDetail.selectedRootUsers.map((u) => (
                    <tr key={u.userId}>
                      <td>{u.alias}</td>
                      <td>{u.email}</td>
                      <td>{u.isAgent ? 'Yes' : 'No'}</td>
                      <td>{u.status}</td>
                      <td>{formatDateYyyyMmDd(u.processedAt)}</td>
                      <td className="text-danger">{u.error ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Card className="mt-4">
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <span className="fw-bold">Verification</span>
                  {!showVerification && (
                    <Button size="sm" color="primary" onClick={() => setShowVerification(true)}>
                      {batchDetail?.status === 'completed' ? 'Verify Results' : 'Preview Changes'}
                    </Button>
                  )}
                  {showVerification && verification && (
                    <Badge color={verification.summary.allPassed ? 'success' : 'danger'} className="fs-6">
                      {verification.summary.allPassed ? 'All Passed' : `${verification.summary.failedCount} Failed`}
                    </Badge>
                  )}
                </CardHeader>
                <CardBody>
                  {!showVerification && (
                    <div className="text-muted">
                      {batchDetail?.status === 'completed'
                        ? 'Click "Verify Results" to verify the migration completed correctly.'
                        : 'Click "Preview Changes" to see expected depth and agreement changes.'}
                    </div>
                  )}
                  {showVerification && isLoadingVerification && (
                    <Alert color="info">Loading verification data...</Alert>
                  )}
                  {showVerification && !isLoadingVerification && !verification && (
                    <Alert color="danger">Unable to load verification</Alert>
                  )}
                  {showVerification && verification && (
                    <>
                      {verification.status !== 'completed' && (
                        <Alert color="warning" className="mb-3">
                          <strong>Note:</strong> This migration is <strong>{verification.status}</strong>. Verification
                          results are only accurate after the migration has been processed.
                        </Alert>
                      )}

                      {/* Migration Flow Visualization */}
                      <div className="d-flex align-items-center justify-content-center bg-dark rounded-3 mb-4 gap-3 bg-opacity-5 py-3">
                        <div className="text-center">
                          <div className="text-muted small text-uppercase mb-1">From</div>
                          <div className="fw-bold fs-5">{verification.sourceAgent?.alias ?? 'No Agent'}</div>
                          <div className="text-muted small">Depth {verification.sourceAgent?.depth ?? '-'}</div>
                        </div>
                        <div className="d-flex flex-column align-items-center px-4">
                          <i className="ri-arrow-right-line fs-2 text-primary" />
                          <span
                            className={`badge ${verification.depthDelta < 0 ? 'bg-success' : verification.depthDelta > 0 ? 'bg-warning' : 'bg-secondary'} mt-1`}
                          >
                            {verification.depthDelta > 0 ? '+' : ''}
                            {verification.depthDelta} depth
                          </span>
                        </div>
                        <div className="text-center">
                          <div className="text-muted small text-uppercase mb-1">To</div>
                          <div className="fw-bold fs-5">{verification.destinationAgent?.alias ?? 'No Agent'}</div>
                          <div className="text-muted small">
                            {verification.destinationAgent ? `Depth ${verification.destinationAgent.depth}` : 'Removed'}
                          </div>
                        </div>
                      </div>

                      {/* Summary Stats */}
                      <div className="row g-3 mb-4">
                        <div className="col-4">
                          <div className="bg-light rounded-3 h-100 p-3 text-center">
                            <div className="fs-3 fw-bold">{verification.summary.totalUsersInSubtrees}</div>
                            <div className="text-muted small">Total Users</div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="bg-success rounded-3 h-100 bg-opacity-10 p-3 text-center">
                            <div className="fs-3 fw-bold text-success">{verification.summary.passedCount}</div>
                            <div className="text-muted small">Passed</div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div
                            className={`${verification.summary.failedCount > 0 ? 'bg-danger bg-opacity-10' : 'bg-light'} rounded-3 h-100 p-3 text-center`}
                          >
                            <div
                              className={`fs-3 fw-bold ${verification.summary.failedCount > 0 ? 'text-danger' : ''}`}
                            >
                              {verification.summary.failedCount}
                            </div>
                            <div className="text-muted small">Failed</div>
                          </div>
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="d-flex border-bottom small text-muted mb-3 gap-4 pb-2">
                        <span className="d-flex align-items-center gap-1">
                          <span className="rounded-circle bg-success d-inline-block" style={{ width: 8, height: 8 }} />
                          Passed
                        </span>
                        <span className="d-flex align-items-center gap-1">
                          <span className="rounded-circle bg-danger d-inline-block" style={{ width: 8, height: 8 }} />
                          Failed
                        </span>
                        <span className="fst-italic">Hover for details</span>
                      </div>

                      {/* User Tree */}
                      <div className="rounded-3 border p-3" style={{ maxHeight: 400, overflowY: 'auto' }}>
                        {verification.migratedUsers.map((user) => (
                          <VerificationTreeNode key={user.userId} node={user} level={0} />
                        ))}
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </ModalBody>
        <ModalFooter className="gap-2">
          {batchDetail?.status === 'pending' && (
            <Button
              color="warning"
              disabled={!selectedBatchId}
              onClick={() => selectedBatchId && setProcessingBatchId(selectedBatchId)}
            >
              Process Now
            </Button>
          )}
          <Button
            outline
            color="dark"
            disabled={!selectedBatchId}
            onClick={() => selectedBatchId && handleExport(selectedBatchId)}
          >
            Export
          </Button>
          <Button outline color="dark" onClick={closeModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <MigrationProgressModal
        batchId={processingBatchId}
        isOpen={Boolean(processingBatchId)}
        onClose={closeProgressModal}
        onComplete={handleProcessComplete}
      />
    </StandardLayout>
  );
};

export default AgentMigrationsPage;
